<template>
  <div id="templates-list-v2" class="container-fluid item-list pb-16 templates-v2-wide">
    <v-list-header
      :title="$t('template.title')"
      icon="compass"
      @open-filters-offcanvas="openOffCanvas('the-template-filters-offcanvas')"
      @emit-close-off-canvas="closeOffCanvas"
      @reload-client-tours="loadTemplates"
    >
      <template #input-search>
        <v-input-text-search
          id="templates-search"
          v-model="name"
          :placeholder="$t('general.shared.search')"
          autocomplete="off"
          class="w-100 me-24"
          input-class="h-auto py-4"
          input-group-text-class="py-4 px-16"
          type="text"
          @update:modelValue="
            name = $event;
            search();
          "
        />
      </template>
    </v-list-header>

    <div class="items-content">
      <div class="filters-container mb-12">
        <v-filters
          v-if="!!filterOptions && filterOptions.length"
          v-model="filterOptions"
          @remove-filter="removeFilter"
        />
      </div>

      <div class="row g-3 mb-12 align-items-end filter-section-compact">
        <div class="col">
          <v-form
            id="activity-filter-status-select"
            :label="$t('general.shared.status')"
            form-type="outline"
            class="mb-0"
          >
            <v-select
              v-model="status"
              :clearable="false"
              :options="statusTypesOptions"
              :reduce="(returnValue: { key: any }) => returnValue.key"
              class="v-select-compact"
              @update:modelValue="doSearch"
            >
              <template v-slot:no-options>
                <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
              </template>
            </v-select>
          </v-form>
        </div>

        <div class="col">
          <v-form
            id="activity-filter-travel-type-select"
            :label="$t('template.travelType')"
            form-type="outline"
            class="mb-0"
          >
            <v-select
              v-model="travelType"
              :clearable="false"
              :options="travelTypeOptions"
              :reduce="(returnValue: { key: any }) => returnValue.key"
              class="v-select-compact"
              @update:modelValue="doSearch"
            >
              <template v-slot:no-options>
                <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
              </template>
            </v-select>
          </v-form>
        </div>

        <div class="col">
          <v-form
            id="activity-filter-modality-select"
            :label="$t('template.modality')"
            form-type="outline"
            class="mb-0"
          >
            <v-select
              v-model="modality"
              :clearable="false"
              :options="modalityOptions"
              :reduce="(returnValue: { key: any }) => returnValue.key"
              class="v-select-compact"
              @update:modelValue="doSearch"
            >
              <template v-slot:no-options>
                <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
              </template>
            </v-select>
          </v-form>
        </div>

        <div class="col">
          <v-form
            id="activity-filter-cms-status-select"
            :label="$t('general.shared.cmsStatus')"
            form-type="outline"
            class="mb-0"
          >
            <v-select
              v-model="cmsStatus"
              :clearable="false"
              :options="cmsStatusOptions"
              :reduce="(returnValue: { key: any }) => returnValue.key"
              class="v-select-compact"
              @update:modelValue="doSearch"
            >
              <template v-slot:no-options>
                <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
              </template>
            </v-select>
          </v-form>
        </div>

        <div class="col">
          <v-form-input
            id="activity-filter-total-days-select"
            v-model="totalDays"
            :label="$t('general.shared.totalDays')"
            autocomplete="off"
            type="number"
            form-type="outline"
            class="form-control-compact mb-0"
            @update:modelValue="search"
            @blur="doSearch"
          />
        </div>
      </div>

      <div class="templates-list-table-shell mt-12">
        <div class="table-responsive d-none d-md-flex table-for-templates">
          <v-table>
            <v-table-header>
              <tr>
                <th>
                  <v-button
                    :class="{ 'sort-active': order.key === 'name' }"
                    class="btn-xs btn-icon btn btn-order"
                    @click="setOrder('name')"
                  >
                    {{ $t("general.shared.name") }}
                    <v-icon
                      :class="{ 'sort-icon-transition': order.key === 'name' && order.order === 'DESC' }"
                      class="ms-8"
                      icon="chevron-down"
                      size="xs"
                    />
                  </v-button>
                </th>

                <th>
                  <v-button
                    :class="{ 'sort-active': order.key === 'travel_type' }"
                    class="btn-xs btn-icon btn btn-order"
                    @click="setOrder('travel_type')"
                  >
                    {{ $t("template.travelType") }}
                    <v-icon
                      :class="{ 'sort-icon-transition': order.key === 'travel_type' && order.order === 'DESC' }"
                      class="ms-8"
                      icon="chevron-down"
                      size="xs"
                    />
                  </v-button>
                </th>

                <th class="text-center">{{ $t("template.modality") }}</th>
                <th>{{ $t("general.shared.origin") }}</th>
                <th>{{ $t("general.shared.destination") }}</th>
                <th class="fit text-center">CMS</th>
                <th class="fit text-end" />
              </tr>
            </v-table-header>

            <v-table-body class="fw-medium">
              <tr v-if="!loaded">
                <td class="position-relative border-0 py-96" colspan="7">
                  <v-loader />
                </td>
              </tr>

              <template v-else-if="!!templates && templates.length > 0">
                <tr v-for="item in templates" :key="item.id">
                  <td class="d-flex align-items-center">
                    <div class="image-container rounded-sm bg-primary me-12">
                      {{ item.name?.charAt(0) }}
                    </div>

                    <router-link
                      v-if="!authStore?.agency?.isCollaborator && !item.deletedAt"
                      :aria-label="item.name"
                      :to="{
                        name: 'activities.setup.show',
                        params: { activitiesId: item.id },
                      }"
                      class="py-4 fw-bold"
                    >
                      {{ item.name }}
                      <span v-if="item.isCollaborator" class="text-muted fw-normal">
                        ({{ $t("general.shared.collaborator") }})
                      </span>
                    </router-link>

                    <a
                      v-else
                      :aria-label="item.name"
                      class="py-4 fw-bold"
                      href="#"
                      @click.prevent="setCurrentTemplate(item)"
                    >
                      {{ item.name }}
                    </a>
                  </td>

                  <td>{{ findConceptByKey("tour.travel_type", item.travelType).name }}</td>
                  <td class="text-center">
                    <span
                      v-if="getModalityLabel(item.modality)"
                      class="activity-modality-badge"
                      :class="getModalityClass(item.modality)"
                    >
                      {{ getModalityLabel(item.modality) }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="text-truncate templates-list-location">{{ item.origin?.name || "—" }}</td>
                  <td class="text-truncate templates-list-location">{{ item.destination?.name || "—" }}</td>

                  <td class="fit text-center">
                    <router-link
                      v-if="item.cmsActivity"
                      :aria-label="item.cmsActivity?.isPublic ? $t('cms.activities.cmsActivityPublished') : $t('cms.activities.cmsActivityDraft')"
                      :to="{
                        name: 'cms.activities.edit',
                        params: { id: item.cmsActivity.id },
                      }"
                      target="_blank"
                      class="btn btn-icon btn-view rounded-pill bg-warning"
                    >
                      <v-icon
                        :icon="item.cmsActivity?.isPublic ? 'eye' : 'eye-blocked'"
                        :class="item.cmsActivity?.isPublic ? 'text-danger' : 'text-warning'"
                        size="sm"
                      />
                    </router-link>
                    <span v-else class="text-muted">—</span>
                  </td>

                  <td class="fit text-end">
                    <div class="d-flex align-items-center gap-4 justify-content-end">
                      <v-button
                        class="btn-view rounded-pill bg-primary"
                        size="xs"
                        variant="icon"
                        :aria-label="$t('template.previewTitle')"
                        @click="openPreviewModal(item)"
                      >
                        <v-icon icon="eye" size="sm" />
                      </v-button>
                      <router-link
                        v-if="!authStore?.agency?.isCollaborator && !item.deletedAt"
                        :aria-label="$t('template.show')"
                        :to="{
                          name: 'activities.setup.show',
                          params: { activitiesId: item.id },
                        }"
                        class="btn btn-icon btn-view rounded-pill bg-secondary"
                      >
                        <v-icon icon="arrow-right" size="sm" />
                      </router-link>

                      <v-button
                        v-else
                        class="btn-view rounded-pill bg-secondary"
                        size="xs"
                        variant="icon"
                        @click="setCurrentTemplate(item)"
                      >
                        <v-icon icon="arrow-right" size="sm" />
                      </v-button>
                    </div>
                  </td>
                </tr>
              </template>

              <template v-else>
                <tr>
                  <td class="rounded-xs py-16 text-center" colspan="7">
                    {{ $t("template.noProducts") }}
                  </td>
                </tr>
              </template>
            </v-table-body>
          </v-table>
        </div>
      </div>

      <div class="item-cards d-block d-md-none">
        <div v-if="!loaded" class="position-relative py-96">
          <v-loader />
        </div>

        <template v-else-if="!!templates && templates.length > 0">
          <div
            v-for="item in templates"
            :key="item.id"
            class="item-card border rounded-xs p-8 mb-12 text-sm"
          >
              <div class="d-flex">
                <div class="image-container rounded-sm bg-primary me-12">
                  {{ item.name?.charAt(0) }}
                </div>

                <div class="w-100 min-w-0">
                  <router-link
                    v-if="!item.deletedAt"
                    :aria-label="item.name"
                    :to="{
                      name: 'activities.setup.show',
                      params: { activitiesId: item.id },
                    }"
                    class="fw-bold"
                  >
                    {{ item.name }}
                  </router-link>

                  <div class="text-xs text-muted mt-4 text-truncate">
                    {{ item.origin?.name || "—" }}
                    <v-icon icon="arrow-right" size="xs" space="px-8" />
                    {{ item.destination?.name || "—" }}
                  </div>
                </div>
              </div>

              <div class="d-flex justify-content-between mt-12 align-items-center">
                <div class="d-flex flex-wrap align-items-center gap-8">
                  <span class="text-xs text-muted">
                    {{ findConceptByKey("tour.travel_type", item.travelType).name }}
                  </span>
                  <span
                    v-if="getModalityLabel(item.modality)"
                    class="activity-modality-badge"
                    :class="getModalityClass(item.modality)"
                  >
                    {{ getModalityLabel(item.modality) }}
                  </span>
                </div>

                <router-link
                  v-if="!item.deletedAt"
                  :aria-label="$t('template.show')"
                  :to="{
                    name: 'activities.setup.show',
                    params: { activitiesId: item.id },
                  }"
                  class="btn btn-icon btn-view"
                >
                  <v-icon icon="arrow-right" size="xs" />
                </router-link>

                <v-button
                  v-else
                  class="btn-view"
                  size="xs"
                  variant="icon"
                  @click="setCurrentTemplate(item)"
                >
                  <v-icon icon="arrow-right" size="xs" />
                </v-button>
              </div>
            </div>
          </template>

        <div v-else class="text-muted text-center py-16">
          {{ $t("template.noProducts") }}
        </div>
      </div>

      <v-pagination
        v-if="!!templates && templates.length > 0"
        :meta="pagination.meta"
        :pagination-links="pagination.links"
        class="mt-32 ms-12 me-12"
        @page-changed="
          changePage($event);
          handlePageChangedEvent($event);
        "
        @per-page-changed="
          perPage = $event;
          doSearch();
        "
      />
    </div>

    <the-template-filters-offcanvas-v2
      v-if="offCanvasToShow === 'the-template-filters-offcanvas'"
      v-model:cmsStatus="cmsStatus"
      v-model:destination="destination"
      v-model:location="location"
      v-model:modality="modality"
      v-model:origin="origin"
      v-model:status="status"
      v-model:totalDays="totalDays"
      v-model:travelType="travelType"
      @closed="closeOffCanvas"
      @selected="doSearch"
    />

    <the-template-archived-offcanvas v-if="offCanvasToShow === 'the-template-archived-offcanvas'"
      :template="currentTemplate" @closed="
        closeOffCanvas();
      cleanCurrentTemplate();
      " @reload-templates="loadTemplates" />

    <the-template-preview-modal v-if="previewModalVisible && previewTemplate" :agency="authStore.agency.slug"
      :activity-id="previewTemplate.id" @closed="closePreviewModal" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { VForm, VFormInput, VInputTextSearch } from "@uniqoders/form";
import qs from "qs";
import querystring from "query-string";
import vSelect from "vue-select";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VTable from "@/components/vendor/basic/table/VTable.vue";
import VTableHeader from "@/components/vendor/basic/table/VTableHeader.vue";
import VTableBody from "@/components/vendor/basic/table/VTableBody.vue";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import api from "@/api";
import withTimeout from "@/helpers/timeOut";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import VListHeader from "@/components/shared/VListHeader.vue";
import VPagination from "@/components/vendor/basic/table/VPagination.vue";
import usePagination from "@/helpers/pagination";
import VFilters from "@/components/vendor/filters/VFilters.vue";
import { useAuthStore } from "@/stores/auth";
import { useAppStore } from "@/stores/app";
import Template from "@/api/objects/Template";
import { findConceptByKey, mapConcepts } from "@/helpers/ConceptHelper";
import { buildModalitySelectOptions, normalizeModalityKey } from "@/helpers/ModalityHelper";
import TheTemplateArchivedOffcanvas from "@/components/template/TheTemplateArchivedOffcanvas.vue";
import TheTemplateFiltersOffcanvasV2 from "@/components/template/V2/TheTemplateFiltersOffcanvasV2.vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import TheTemplatePreviewModal from '@/components/template/TheTemplatePreviewModal.vue';

const templates = ref<Template[]>([]);
const status = ref<string>("active");
const name = ref<string>("");
const destination = ref<string>("");
const location = ref<string>("");
const origin = ref<string>("");
const travelType = ref<string>("");
const modality = ref<string>("");
const cmsStatus = ref<string>("");
const totalDays = ref<string>("");

const filters = ref([
  {
    variable: "status",
    key: "status",
    labelAux: "general.shared.filterStatus",
    conceptName: "app.states",
    multiple: false,
    isConcept: true,
  },
  {
    variable: "name",
    key: "name",
    labelAux: "general.shared.filterSearch",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "destination",
    key: "destination",
    labelAux: "general.shared.filterDestination",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "location",
    key: "location",
    labelAux: "general.shared.filterLocation",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "origin",
    key: "origin",
    labelAux: "general.shared.filterOrigin",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "travelType",
    key: "travel_type",
    labelAux: "general.shared.filterTravelType",
    conceptName: "tour.travel_type",
    multiple: false,
    isConcept: true,
  },
  {
    variable: "modality",
    key: "modality",
    labelAux: "general.shared.filterModality",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "cmsStatus",
    key: "cms_status",
    labelAux: "general.shared.filterCmsStatus",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "totalDays",
    key: "total_days",
    labelAux: "general.shared.filterTotalDays",
    multiple: false,
    isConcept: false,
  },
]);

const filterRefs = {
  status,
  name,
  destination,
  location,
  origin,
  travelType,
  modality,
  cmsStatus,
  totalDays,
};
const order = ref<Record<string, string>>({
  key: "",
  order: "",
});
const perPage = ref<number>(15);
const page = ref<number>(1);
const timers = ref<{ name: any }>({ name: null });
const loaded = ref<boolean>(false);
const currentTemplate = ref<Template | null>(null);
const filterOptions = ref<Record<string, any>[]>([]);
const previewModalVisible = ref(false);
const previewTemplate = ref<Template | null>(null);

const authStore = useAuthStore();
const { offCanvasToShow, openOffCanvas, closeOffCanvas } = useOffCanvasUtils();
const { pagination, changePage, setResponse } = usePagination();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const appStore = useAppStore();

onMounted(async () => {
  await setQueryParametersAsFilters();
  await doSearch();
});

const buildOptions = (key: string) => {
  if (appStore.concepts && appStore.concepts[key]) {
    const list = mapConcepts(appStore.concepts[key]);

    list.unshift({
      key: "",
      label: t("general.shared.all"),
      permission: null,
      value: null,
      order: 0,
      status: "active",
      icon: "",
      color: "",
      shortName: "all",
    });

    return list;
  }
  return [];
};

const travelTypeOptions = computed(() => buildOptions("tour.travel_type"));

const statusTypesOptions = computed(() => {
  if (appStore.concepts && appStore.concepts["app.states"]) {
    return mapConcepts(appStore.concepts["app.states"]);
  }
  return [];
});

const modalityOptions = computed(() => [
  { key: "", label: t("general.shared.all") },
  ...buildModalitySelectOptions(t),
]);

const cmsStatusOptions = computed(() => [
  { key: "", label: t("general.shared.all") },
  { key: "published", label: t("cms.activities.cmsActivityPublished") },
  { key: "draft", label: t("cms.activities.cmsActivityDraft") },
  { key: "none", label: t("general.shared.cmsStatusNone") },
]);

const getCmsStatusLabel = (value: string) => {
  if (value === "published") {
    return t("cms.activities.cmsActivityPublished");
  }
  if (value === "draft") {
    return t("cms.activities.cmsActivityDraft");
  }
  if (value === "none") {
    return t("general.shared.cmsStatusNone");
  }
  return value;
};

const search = () => {
  clearTimeout(timers.value.name);

  timers.value.name = withTimeout(
    async () => {
      await doSearch();
    },
    timers.value.name,
    1000
  );
};

const setQueryParametersAsFilters = async () => {
  const query = route.query;
  const {
    filters: filterQuery,
    order: orderQuery,
    perPage: perPageQuery,
    page: pageQuery,
    status: statusQuery,
  }: any = qs.parse(qs.stringify(query));

  if (filterQuery) {
    filters.value.forEach((filter) => {
      if (filterQuery[filter.key] !== undefined && filter.variable in filterRefs) {
        filterRefs[filter.variable as keyof typeof filterRefs].value = filterQuery[filter.key];
      }
    });
  }

  if (orderQuery) {
    order.value = orderQuery;
  }

  const pageNumber = parseInt(pageQuery, 10);
  if (!Number.isNaN(pageNumber)) {
    page.value = pageNumber;
  }

  const perPageNumber = parseInt(perPageQuery, 10);
  if (!Number.isNaN(perPageNumber)) {
    perPage.value = perPageNumber;
  }

  if (statusQuery) {
    status.value = statusQuery;
  }
};

const setFiltersAsQueryParameters = async () => {
  const filtersPayload: Record<string, any> = {};

  filters.value.forEach((filter) => {
    const value = filterRefs[filter.variable as keyof typeof filterRefs]?.value;
    if (value !== undefined && value !== null && value !== "" && value !== false) {
      filtersPayload[filter.key] = value;
    }
  });

  const queryObject: any = {
    filters: filtersPayload,
    perPage: perPage.value,
    status: status.value,
    page: page.value,
  };

  if (order.value?.key) {
    queryObject.order = order.value;
  }

  await setFilters();

  let query: any = qs.stringify(queryObject, { encode: false });
  query = querystring.parse(query);

  router.push({
    name: "activities.index",
    query,
  });
};

const doSearch = async () => {
  await setFiltersAsQueryParameters();
  await loadTemplates();
};

const loadTemplates = async (pageNumber?: number) => {
  try {
    loaded.value = false;

    const filtersPayload: Record<string, any> = {};

    filters.value.forEach((filter) => {
      const value = filterRefs[filter.variable as keyof typeof filterRefs]?.value;
      if (value !== undefined && value !== null && value !== "" && value !== false) {
        filtersPayload[filter.key] = value;
      }
    });

    const query: any = {
      filters: filtersPayload,
      order: order.value,
      perPage: perPage.value,
      status: status.value,
      page: pageNumber ?? page.value,
    };

    if (authStore.agency) {
      const response = await api.activityService.allPaginated(authStore.agency.slug, query);
      setResponse(response);
      templates.value = response.data;
    }
  } catch (e: any) {
    console.error(e);
  } finally {
    loaded.value = true;
  }
};

const cleanCurrentTemplate = () => {
  currentTemplate.value = null;
};

const handlePageChangedEvent = async (page: number) => {
  await loadTemplates(page);
};

const setOrder = (field: string) => {
  if (!order.value.order) {
    order.value.order = "ASC";
  } else {
    order.value.order = order.value.order === "ASC" ? "DESC" : "ASC";
  }
  order.value.key = field;

  search();
};

const getFilterDisplayValue = (variable: string, option: any) => {
  if (variable === "modality") {
    return getModalityLabel(option) || option;
  }
  if (variable === "cmsStatus") {
    return getCmsStatusLabel(option);
  }
  return option;
};

const setFilters = () => {
  filterOptions.value = filters.value.flatMap((filter) => {
    const { key, multiple, isConcept, labelAux, conceptName, variable } = filter;
    const selected = filterRefs[variable as keyof typeof filterRefs]?.value;
    const options = multiple ? selected || [] : [selected];

    return options
      .filter((opt: any) => opt !== undefined && opt !== null && opt !== "" && opt !== false)
      .map((option: any) => {
        const value = isConcept ? findConceptByKey(`${conceptName}`, option) : option;
        const displayValue = isConcept ? value?.name : getFilterDisplayValue(variable, option);

        return {
          key,
          value: isConcept ? value?.key : value,
          label: t(`${labelAux}`, { value: displayValue }),
        };
      });
  });
};

const removeFilter = (filter: Record<string, string>) => {
  filters.value.forEach((filterAux) => {
    if (filterAux.key !== filter.key) {
      return;
    }

    const refVar = filterRefs[filterAux.variable as keyof typeof filterRefs];
    if (!refVar) {
      return;
    }

    refVar.value = "";
  });

  if (filter.key === "status") {
    status.value = "active";
  }

  doSearch();
};

const setCurrentTemplate = (template) => {
  currentTemplate.value = template;

  if (offCanvasToShow.value !== "the-template-archived-offcanvas") {
    openOffCanvas("the-template-archived-offcanvas");
  }
};

function openPreviewModal(template: Template) {
  previewTemplate.value = template;
  previewModalVisible.value = true;
}

function closePreviewModal() {
  previewModalVisible.value = false;
  previewTemplate.value = null;
}

const getModalityLabel = (modality: string | null | undefined) => {
  const key = normalizeModalityKey(modality);
  if (key === "self-guided") {
    return t("cms.activities.modalityOptions.selfGuided");
  }
  if (key === "group") {
    return t("cms.activities.modalityOptions.group");
  }
  return "";
};

const getModalityClass = (modality: string | null | undefined) => {
  const key = normalizeModalityKey(modality);
  return key ? `activity-modality-badge--${key}` : "";
};

onUnmounted(() => {
  cleanCurrentTemplate();
});
</script>

<style scoped lang="scss">
.templates-v2-wide {
  max-width: 1550px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

@media (min-width: 1400px) {
  .templates-v2-wide {
    padding-left: 1.75rem;
    padding-right: 1.75rem;
  }
}

.templates-list-table-shell {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.table-for-templates :deep(.table) {
  border: 0 !important;
  margin-bottom: 0 !important;
  font-size: 0.9rem;
}

.table-for-templates :deep(thead th) {
  background: #f8f9fa;
  font-size: 0.75rem;
  letter-spacing: 0.3px;
  padding: 0.7rem 0.75rem;
  border: 0 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.table-for-templates :deep(tbody td) {
  padding: 0.6rem 0.75rem;
  border: 0 !important;
}

.table-for-templates :deep(tbody tr:not(:last-child) td) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
}

.table-for-templates :deep(.btn-view .icon),
.table-for-templates :deep(.btn-view .mi) {
  font-size: 14px !important;
}

.table-for-templates :deep(.btn-view) {
  width: 30px !important;
  height: 30px !important;
}

.templates-list-location {
  max-width: 9rem;
}

.activity-modality-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 20px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  border: 1px solid transparent;
  letter-spacing: 0.02em;
}

.activity-modality-badge--self-guided {
  background: #e3f2fd;
  color: #1565c0;
  border-color: rgba(33, 150, 243, 0.35);
}

.activity-modality-badge--group {
  background: rgba(238, 42, 123, 0.12);
  color: #ad1457;
  border-color: rgba(238, 42, 123, 0.35);
}

.filter-section-compact {
  background: #f8f9fa;
  padding: 9px 4px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  font-weight: 600;
  margin: 0;
}

.v-select-compact :deep(.vs__dropdown-toggle) {
  min-height: 32px;
  padding: 0;
}

.v-select-compact :deep(.vs__selected) {
  margin: 2px;
  font-size: 0.875rem;
  font-weight: 600;
}

.v-select-compact :deep(.vs__actions) {
  padding: 2px 6px;
}

.form-control-compact :deep(.v-form-input-field) {
  height: 32px;
  padding: 4px 8px;
  font-size: 0.875rem;
  font-weight: 600;
}

:deep(.v-form-label) {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 2px;
  letter-spacing: 0.4px;
  display: block;
}
</style>
